<template>
  <div>
    <modal :title="$t('Profile')" :displayed="displayed" size="fullscreen" @close="$emit('close')">
      <button
        slot="title-action"
        v-t
        type="button"
        form="profileForm"
        @click.prevent="submitProfile"
      >
        Save
      </button>
      <vue-form
        ref="profileForm"
        slot="body"
        :state="profileState"
        name="profileForm"
        enctype="multipart/form-data"
        class="profile-form"
        @submit.prevent="submitProfile"
      >
        <article v-if="!external">
          <header class="advisor-profile">
            <h5 v-t>Company Profile (optional)</h5>
            <p v-t>Add company info for payment and identification reasons.</p>
          </header>
          <section class="half-width">
            <validate class="field">
              <label v-t for="companyname">Company name</label>
              <input
                id="companyname"
                v-model="companyName"
                type="text"
                name="companyname"
                maxlength="256"
                :class="{ invalid: inputError.companyname.$invalid }"
                @blur="updateError('companyname')"
              >
            </validate>
          </section>
          <section>
            <validate class="field">
              <label v-t for="companywebsite">Company website</label>
              <input
                id="companywebsite"
                v-model="companyWebsite"
                type="text"
                name="companywebsite"
                maxlength="256"
              >
            </validate>
            <validate class="field">
              <label v-t for="companyphone">Company phone</label>
              <input id="companyphone" v-model="companyPhone" type="text" name="companyphone">
            </validate>
          </section>
          <section class="half-width">
            <validate class="field">
              <label v-t for="streetaddress">Street address</label>
              <input id="streetaddress" v-model="streetAddress" type="text" name="streetaddress">
            </validate>
          </section>
          <section>
            <validate class="field">
              <label v-t for="city">City</label>
              <input id="city" v-model="city" type="text" name="city">
            </validate>
            <div class="inline-input full">
              <validate class="field">
                <label v-t for="province">Province</label>

                <select
                  id="province"
                  v-model="province"
                  type="text"
                  name="province"
                  class="select-menu"
                >
                  <option value />
                  <option v-t value="AB">Alberta</option>
                  <option v-t value="BC">British Columbia</option>
                  <option v-t value="MB">Manitoba</option>
                  <option v-t value="NB">New Brunswick</option>
                  <option v-t value="NL">Newfoundland and Labrador</option>
                  <option v-t value="NS">Nova Scotia</option>
                  <option v-t value="ON">Ontario</option>
                  <option v-t value="PE">Prince Edward Island</option>
                  <option v-t value="QC">Quebec</option>
                  <option v-t value="SK">Saskatchewan</option>
                  <option v-t value="NT">Northwest Territories</option>
                  <option v-t value="NU">Nunavut</option>
                  <option v-t value="YT">Yukon</option>
                </select>
              </validate>
              <validate class="field">
                <label v-t for="postalcode">Postal code</label>
                <input
                  id="postalcode"
                  v-model="postalCode"
                  type="text"
                  name="postalcode"
                  maxlength="7"
                >
              </validate>
            </div>
          </section>
          <hr>
        </article>

        <article>
          <header class="advisor-profile">
            <h5 v-t>Advisor Profile (required)</h5>
            <p v-t>Your advisor profile is visible to your clients.</p>
          </header>
          <section class="inline-input full">
            <validate class="field">
              <label v-t for="firstname">First name</label>
              <input
                id="firstname"
                v-model="profileFirstName"
                :disabled="external"
                type="text"
                name="firstname"
                :class="{ invalid: inputError.firstname.$invalid }"
                :required="external ? false : true"
                @blur="updateError('firstname')"
              >
              <div v-if="inputError.firstname.$error" class="error-message">
                <div v-if="inputError.firstname.$error.required" v-t>Required</div>
              </div>
            </validate>
            <validate class="field">
              <label v-t for="lastname">Last name</label>
              <input
                id="lastname"
                v-model="profileLastName"
                :disabled="external"
                type="text"
                name="lastname"
                :class="{ invalid: inputError.lastname.$invalid }"
                :required="external ? false : true"
                @blur="updateError('lastname')"
              >
              <div v-if="inputError.lastname.$error" class="error-message">
                <div v-if="inputError.lastname.$error.required" v-t>Required</div>
              </div>
            </validate>
          </section>

          <section class="inline-input full">
            <validate class="field">
              <label v-t for="email" :class="{ invalid: inputError.email }">Email</label>
              <input
                id="email"
                v-model="profileEmail"
                :disabled="external || authWB"
                type="email"
                name="email"
                :class="{ invalid: inputError.email.$invalid }"
                :required="external ? false : true"
                @blur="updateError('email')"
              >
              <div
                v-if="inputError.email.$error || inputError.email.$emailExists"
                class="error-message"
              >
                <div v-if="inputError.email.$error && inputError.email.$error.required" v-t>Required</div>
                <div
                  v-if="inputError.email.$error && inputError.email.$error.email"
                  v-t
                >
                  Invalid email
                </div>
                <div
                  v-if="inputError.email.$emailExists"
                  v-t
                >
                  This email address is already in our system.
                </div>
              </div>
              <div class="help-message">
                <a v-if="authWB" :href="emailChangeUrl">Change Email</a>
              </div>
            </validate>
            <validate class="field">
              <label v-t for="phone">Phone number</label>
              <input
                id="phone"
                v-model="profilePhone"
                type="tel"
                name="phone"
                :required="external ? false : true"
                :class="{ invalid: inputError.phone.$invalid }"
                @blur="updateError('phone')"
              >
              <div v-if="inputError.phone.$error" class="error-message">
                <div v-if="inputError.phone.$error.required" v-t>Required</div>
              </div>
            </validate>
          </section>
          <section>
            <div class="profile-image">
              <div class="profile-image-input">
                <legend>
                  {{ getImageTypeText() }}
                  <span v-t>(optional)</span>
                </legend>
                <div class="image" :class="{'image-logo':isImageLogo}">
                  <loading-spinner v-if="imageLoading" :loading="imageLoading" />
                  <img v-else-if="imageUrl" :src="imageUrl" :alt="getImageTypeText()">
                  <img v-else src="~@imagesBrand/profile.svg" :alt="getImageTypeText()">
                  <validate class="file-upload">
                    <input
                      id="photo-upload"
                      ref="file"
                      type="file"
                      name="photo-upload"
                      accept="image/*"
                      @change="photoUpload($event)"
                    >
                  </validate>
                </div>
              </div>

              <div class="profile-image-actions">
                <label for="photo-upload" class="button flat icon before">
                  <svg viewBox="0 0 32 32">
                    <!-- eslint-disable-next-line max-len -->
                    <path d="M28,28H4c-1.7,0-3-1.3-3-3V10c0-1.7,1.3-3,3-3h4.5l1.1-1.7C10.1,4.5,11.1,4,12.1,4h7.9c1,0,1.9,0.5,2.5,1.3L23.5,7H28 c1.7,0,3,1.3,3,3v15C31,26.7,29.7,28,28,28z M4,9c-0.6,0-1,0.4-1,1v15c0,0.6,0.4,1,1,1h24c0.6,0,1-0.4,1-1V10c0-0.6-0.4-1-1-1h-5.5 l-1.7-2.6C20.6,6.2,20.3,6,19.9,6h-7.9c-0.3,0-0.6,0.2-0.8,0.4L9.5,9H4z" />
                    <!-- eslint-disable-next-line max-len -->
                    <path d="M16,24.1c-3.9,0-7.1-3.2-7.1-7.1s3.2-7.1,7.1-7.1s7.1,3.2,7.1,7.1S19.9,24.1,16,24.1z M16,12.1c-2.7,0-4.9,2.2-4.9,4.9 s2.2,4.9,4.9,4.9s4.9-2.2,4.9-4.9S18.7,12.1,16,12.1z" />
                  </svg>
                  <span v-t>Change photo</span>
                </label>

                <button v-if="imageUrl" type="button" class="flat neg icon before" @click="removePhoto">
                  <svg viewBox="0 0 32 32">
                    <!-- eslint-disable-next-line max-len -->
                    <path d="M29,5h-7V3c0-1.1-0.9-2-2-2h-8c-1.1,0-2,0.9-2,2v2H3v2h3v21c0,1.7,1.3,3,3,3h14c1.7,0,3-1.3,3-3V7h3V5z M12,3h8v2h-8V3z M24,28c0,0.6-0.4,1-1,1H9c-0.6,0-1-0.4-1-1V7h16V28z" />
                    <rect x="12" y="10" width="2" height="16" />
                    <rect x="18" y="10" width="2" height="16" />
                  </svg>
                  <span v-t>Remove photo</span>
                </button>

                <input
                  id="photo-profile"
                  v-model="imageType"
                  type="radio"
                  name="photo-type"
                  value="photo"
                >
                <label for="photo-profile" class="profile-image-type"><span v-t>Profile Photo</span></label>
                <div v-show="imageType==='photo'" class="notice">
                  <p v-t class="notice-content">Minimum size requirements are 300x300 pixels. Images will be cropped to a 1:1 ratio.</p>
                </div>

                <input
                  id="photo-logo"
                  v-model="imageType"
                  type="radio"
                  name="photo-type"
                  value="logo"
                >
                <label for="photo-logo"><span v-t>Logo Image</span></label>
                <div v-show="imageType==='logo'" class="notice">
                  <p v-t class="notice-content">Minimum size requirements are 320x200 pixels. Images will need to be a landscape ratio.</p>
                </div>
              </div>
            </div>
            <div class="profile-image-errors">
              <p v-if="messageError" class="error-message">
                <svg viewBox="0 0 32 32">
                  <!-- eslint-disable-next-line max-len -->
                  <path d="M16,4c6.6,0,12,5.4,12,12s-5.4,12-12,12S4,22.6,4,16S9.4,4,16,4 M16,2C8.3,2,2,8.3,2,16s6.3,14,14,14s14-6.3,14-14 S23.7,2,16,2L16,2z" />
                  <rect x="15" y="7" width="2" height="12" />
                  <circle cx="16" cy="22.5" r="1.5" />
                </svg>
                <span>{{ messageError }}</span>
              </p>

              <p v-if="messageWarning" class="warning-message">
                <svg viewBox="0 0 32 32">
                  <!-- eslint-disable-next-line max-len -->
                  <path d="M16.1,4c0.7,0,1.3,0.3,1.7,0.9l11.9,18.9c0.6,0.9,0.2,1.7,0.1,2c-0.2,0.3-0.7,1-1.8,1H4.1c-1.1,0-1.6-0.7-1.8-1 c-0.2-0.3-0.5-1.1,0.1-2L14.4,5C14.7,4.4,15.4,4,16.1,4 M16.1,2c-1.3,0-2.6,0.6-3.4,1.9L0.8,22.8C-0.9,25.5,1,29,4.1,29H28 c3.1,0,5.1-3.5,3.4-6.1L19.5,3.9C18.7,2.7,17.4,2,16.1,2L16.1,2z" />
                  <rect x="15" y="8" width="2" height="12" />
                  <circle cx="16" cy="23.5" r="1.5" />
                </svg>
                <span>{{ messageWarning }}</span>
              </p>
            </div>
          </section>
          <hr>
          <section class="language-selection">
            <h4 v-t>Language</h4>
            <p v-t>Set your preferred language for this site and any future emails from us.</p>

            <input
              id="english"
              v-model="languagePreference"
              type="radio"
              name="english"
              value="en"
              @change="updateLanguage"
            >
            <label for="english">English</label>

            <input
              id="french"
              v-model="languagePreference"
              type="radio"
              name="french"
              value="fr_CA"
              @change="updateLanguage"
            >

            <label for="french">Français</label>
          </section>
        </article>
      </vue-form>
    </modal>
    <confirm :displayed="deleteConfirmationDisplay" class="remove-photo-modal">
      <p slot="body" v-t>Are you sure you want to remove your current image?</p>
      <button slot="footer" v-t type="button" @click="confirmRemove">Remove</button>
      <button slot="footer" v-t type="button" class="subtle" @click="keepPhoto">Keep</button>
    </confirm>
    <confirm :displayed="deleteConfirmationDisplaySwitch" class="switch-photo-modal">
      <p slot="body" v-t>You will need to delete your existing image to make this change. Are you sure you want to remove your current image?</p>
      <button slot="footer" v-t type="button" @click="confirmRemove">Remove</button>
      <button slot="footer" v-t type="button" class="subtle" @click="keepSwitchedPhoto">Keep</button>
    </confirm>
  </div>
</template>

<script>
import { modal, confirm, loadingSpinner } from '@/components/layout';
import { mapGetters, mapActions } from '@/lib/vue';
import { uploadPhoto } from '@/lib/api';
import { apiUrl } from '@/lib/utils/constants';
import { changeLanguage } from '@wealthbar/i18n';
import getBrandConfig from '@/lib/utils/get_brand_config';
import errorStore from '@/store/error';

import '@/lib/icons/camera';
import '@/lib/icons/alert-triangle';
import '@/lib/icons/alert-circle';

const brandConfig = getBrandConfig();
const _ = {
  brandConfig,
  uploadPhoto,
  errorStore,
};

export default {
  _,
  name: 'Profile',
  components: {
    modal,
    confirm,
    loadingSpinner,
  },
  props: {
    displayed: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      deleteConfirmationDisplay: false,
      deleteConfirmationDisplaySwitch: false,
      photoContext: false,
      profileState: {},
      inputError: {
        firstname: {},
        lastname: {},
        phone: {},
        email: {},
        companyname: {},
      },
      authWB: false,
      required: true,
      companyName: null,
      companyWebsite: null,
      companyPhone: null,
      streetAddress: null,
      city: null,
      province: null,
      postalCode: null,
      profileFirstName: null,
      profileLastName: null,
      profilePhone: null,
      profileEmail: null,
      imageUrl: null,
      imageType: 'photo',
      imageTypePrev: 'photo',
      messageWarning: false,
      messageError: false,
      imageLoading: false,
      external: false,
      languagePreference: null,
    };
  },
  computed: {
    ...mapGetters(['account']),
    emailChangeUrl() { return `${apiUrl.host}/app/email/new`; },
    isImageLogo() { return this.imageUrl ? this.imageUrl.includes('advisor_logo') : false; },
    isPhotoProfile() { return this.imageUrl ? this.imageUrl.includes('advisor_photo') : false; },
  },
  watch: {
    async displayed(value) {
      if (value === true) {
        try {
          await this.fetchAccount();
          this.profileFirstName = this.account.firstName;
          this.profileLastName = this.account.lastName;
          this.profilePhone = this.account.phone;
          this.profileEmail = this.account.email;
          this.imageUrl = this.account.photoUrl;
          this.companyName = this.account.companyName;
          this.companyWebsite = this.account.companyWebsite;
          this.companyPhone = this.account.companyPhone;
          this.streetAddress = this.account.streetAddress;
          this.city = this.account.city;
          this.postalCode = this.account.postalCode;
          this.province = this.account.province;
          this.languagePreference = this.account.languagePreference;
          this.messageError = false;
          this.messageWarning = false;
          this.inputError = {
            firstname: {},
            lastname: {},
            phone: {},
            email: {},
            companyname: {},
          };
        } catch (exception) {
          _.errorStore.addError();
        }
      }
    },
    imageType(newVal, oldVal) {
      if (this.imageUrl && this.imageTypePrev !== newVal) {
        this.imageTypePrev = oldVal;
        this.deleteConfirmationDisplaySwitch = true;
      }
      this.resetImageErrors();
    },
  },
  async created() {
    try {
      await this.fetchAccount();
      this.authWB = (_.brandConfig.auth === 'wb');
      this.external = _.brandConfig.externalProfile;
      this.profileFirstName = this.account.firstName;
      this.profileLastName = this.account.lastName;
      this.profilePhone = this.account.phone;
      this.profileEmail = this.account.email;
      this.imageUrl = this.account.photoUrl;
      this.imageType = this.isImageLogo ? 'logo' : 'photo';
      this.imageTypePrev = this.isImageLogo ? 'logo' : 'photo';
      this.companyName = this.account.companyName;
      this.companyWebsite = this.account.companyWebsite;
      this.companyPhone = this.account.companyPhone;
      this.streetAddress = this.account.streetAddress;
      this.city = this.account.city;
      this.postalCode = this.account.postalCode;
      this.province = this.account.province;
      this.languagePreference = this.account.languagePreference;
    } catch (exception) {
      _.errorStore.addError();
    }
  },
  methods: {
    ...mapActions([
      'fetchAccount',
      'updateAccount',
      'updateAdvisorPhoto',
      'deleteAdvisorPhoto',
    ]),
    updateLanguage(e) {
      this.languagePreference = e.target.value;
    },
    updateError(fieldName) {
      this.inputError[fieldName] = Object.assign(
        {},
        this.profileState[fieldName],
      );
      this.inputError[fieldName].$error = Object.assign(
        {},
        this.profileState[fieldName].$error,
      );
    },
    getImageTypeText() {
      return this.imageType === 'logo' ? this.$t('Logo Image') : this.$t('Profile Photo');
    },
    updateAllErrors() {
      this.updateError('firstname');
      this.updateError('lastname');
      this.updateError('phone');
      this.updateError('email');
      this.updateError('companyname');
    },
    resetImageErrors() {
      this.messageError = false;
      this.messageWarning = false;
    },
    setErrorMessage(response) {
      if (response.message) {
        this.messageError = this.$t('Sorry, that file type is not supported. Please upload a JPG or PNG file instead.');
      } else if (response.ratioError && this.imageType === 'logo') {
        this.messageError = this.$t("Sorry, your image doesn't meet our requirements. Please make sure the image is landscape ratio (width is larger than height).");
      } else if (response.imageTooBig) {
        this.messageError = this.$t('Sorry, that photo is too big. The maximum file size is 5MB.');
      } else if (response.notification) {
        this.messageWarning = this.$t('Heads up! This photo is small and may look poor in client communications.');
      } else {
        this.resetImageErrors();
      }
    },
    async photoUpload(e) {
      this.imageLoading = true;
      this.resetImageErrors();
      this.imageTypePrev = this.imageType;
      const files = e.target.files;
      if (files.length) {
        const size = parseFloat(files[0].size / 1024).toFixed(2);
        // 5mb max file size
        if (size >= 5000) {
          this.setErrorMessage({ imageTooBig: true });
          this.imageLoading = false;
          // This resets the field so we can show the error again if they try to reupload a large file
          if (this.$refs.file) {
            this.$refs.file.value = '';
          }
        } else {
          let response = null;
          try {
            response = await _.uploadPhoto(files[0], this.imageType);
          } catch (exception) {
            this.imageLoading = false;
            _.errorStore.addError();
            // This resets the field so we can upload the same file again if we want to
            this.$refs.file.value = '';
            return;
          }
          // This resets the field so we can upload the same file again if we want to
          this.$refs.file.value = '';
          this.setErrorMessage(response);
          if (this.messageWarning || this.messageError) {
            this.imageLoading = false;
            return;
          }
          const account = Object.assign({}, this.account, {
            photoUrl: response.photoUrl,
          });
          try {
            await this.updateAdvisorPhoto(account);
          } catch (exception) {
            this.imageLoading = false;
            _.errorStore.addError();
            return;
          }
          this.imageUrl = response.photoUrl;
          this.imageLoading = false;
        }
      }
    },
    removePhoto() {
      this.deleteConfirmationDisplay = !this.deleteConfirmationDisplay;
    },
    async confirmRemove() {
      try {
        await this.deleteAdvisorPhoto();
      } catch (exception) {
        _.errorStore.addError();
        this.deleteConfirmationDisplay = false;
        this.deleteConfirmationDisplaySwitch = false;
        return;
      }
      this.imageUrl = null;
      this.resetImageErrors();
      this.deleteConfirmationDisplay = false;
      this.deleteConfirmationDisplaySwitch = false;
    },
    keepPhoto() {
      this.deleteConfirmationDisplay = false;
    },
    keepSwitchedPhoto() {
      this.imageType = this.imageTypePrev;
      this.deleteConfirmationDisplaySwitch = false;
    },
    async submitProfile() {
      if (this.profileState.$invalid) {
        this.updateAllErrors();
      } else {
        const account = Object.assign(this.account, {
          firstName: this.profileFirstName,
          lastName: this.profileLastName,
          phone: this.profilePhone,
          email: this.profileEmail,
          companyName: this.companyName,
          companyWebsite: this.companyWebsite,
          companyPhone: this.companyPhone,
          streetAddress: this.streetAddress,
          city: this.city,
          postalCode: this.postalCode,
          province: this.province,
          languagePreference: this.languagePreference,
        });
        this.resetImageErrors();
        try {
          const lang = this.languagePreference && this.languagePreference.slice(0, 2) === 'fr' ? 'fr-CA' : 'en-CA';
          await changeLanguage(lang);
          await this.updateAccount(account);
          this.$emit('close');
        } catch (exception) {
          if (exception.message === 'emailExists') {
            this.inputError.email = { $emailExists: true };
          } else {
            _.errorStore.addError();
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
article {
  margin: 0 0.5rem;
}

input + .error-message + .help-message {
  margin: -1.25rem 0 1.25rem;
}

.profile-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  @media #{$screen-width-medium} {
    flex-direction: row;
    align-items: start;
  }
}

.profile-image-errors {
  margin: 0 auto;
  padding: 0 1rem;
  .error-message svg {
    width:2rem;
    height: 1.25rem;
    @media #{$screen-width-medium} {
      width:1rem;
      height: 1rem;
    }
  }
  .warning-message svg {
    width:2rem;
    @media #{$screen-width-medium} {
      width:1rem;
    }
  }
}

.profile-image-input {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 195px;
  @media #{$screen-width-medium} {
    margin-right: 1.5rem;
  }
  legend {
    margin-bottom: 0.5rem;
  }
}

.profile-image-actions{
  margin-top:0.8rem;
  display:flex;
  flex-direction: column;
  align-items: center;
  @media #{$screen-width-medium} {
    margin-top: 0;
    align-items: start;
  }
  .profile-image-type {
    margin-top:0.35rem;
  }
  input[type="radio"] + label{
    min-width: 135px;
  }
  .notice{
    .notice-content{
      margin: 0.5rem 0.5rem;
      text-align:center;
      font-size: 0.9rem;
      @media #{$screen-width-medium} {
        text-align: left;
      }
    }
    margin-bottom: 0.75rem;
  }
  label{
    &.button {
      margin-bottom: 0;
      padding-top: 0.15rem;
    }
  }
}

.image {
  width: 7rem;
  height: 7rem;
  display: flex;
  &.image-logo{
    width: 10rem;
    img {
      border-radius: 0;
      object-fit: contain;
    }
  }
  .file-upload,
  .file-upload input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
  }
  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
  .spinner {
    margin: auto;
  }
}

.language-selection {
  margin: 0 0 5.5rem 0;
}

.half-width {
  @media #{$screen-width-small} {
    width: 50%;
  }
}

button[type="button"].subtle {
  margin-left: 0.75rem;
}
</style>
